/** Targets & Profile: about you, your daily burn, daily targets, body composition and the goal. */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Icon } from '../components/Icon';
import { BodyCompCard } from '../components/profile/BodyCompCard';
import { EnergyCard } from '../components/profile/EnergyCard';
import { GoalCard } from '../components/profile/GoalCard';
import { MacroPlanner } from '../components/profile/MacroPlanner';
import { Card, Spinner } from '../components/ui';
import { ageOn } from '../lib/dates';
import { errorMessage } from '../lib/logger';
import type { Profile, ProfileUpdate, Sex, UnitSystem } from '../lib/types';
import { cmToFeetInches, feetInchesToCm } from '../lib/units';
import { useApp } from '../state/app';

const str = (v: number | null | undefined, digits: number = 0): string => (v === null || v === undefined ? '' : String(Math.round(v * 10 ** digits) / 10 ** digits));
const numOrNull = (s: string): number | null => { const t = s.trim(); if (t === '') return null; const n = Number(t); return Number.isFinite(n) ? n : null; };

function timeZones(): string[] {
  try {
    const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    if (fn) return fn('timeZone');
  } catch { /* older browsers */ }
  return ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'UTC'];
}

export function ProfilePage(): ReactElement {
  const { settingsStored } = useApp();
  const [recommended, setRecommended] = useState<number | null>(null);
  const onPlan = useCallback((kcal: number | null) => setRecommended(kcal), []);
  return (
    <div className="stack">
      {settingsStored === 'device' && (
        <div className="note warn">
          These settings are saved in this browser only until the database update runs (migration <code>20260925220000_diet_energy_diary_submissions.sql</code>). After that they follow you to every device.
        </div>
      )}
      <div className="grid cols-main">
        <div className="stack">
          <AboutYouCard />
          <EnergyCard />
          <MacroPlanner recommendedKcal={recommended} />
        </div>
        <div className="stack">
          <BodyCompCard />
          <GoalCard onPlan={onPlan} />
        </div>
      </div>
    </div>
  );
}

interface AboutForm {
  display_name: string; sex: Sex | ''; birth_date: string; heightCm: string; heightFt: string; heightIn: string;
  unit_system: UnitSystem; timezone: string; cycle_start: string; cycle_length: string;
}

function formFromProfile(p: Profile): AboutForm {
  const fi = p.height_cm !== null ? cmToFeetInches(p.height_cm) : null;
  return {
    display_name: p.display_name ?? '', sex: p.sex ?? '', birth_date: p.birth_date ?? '',
    heightCm: str(p.height_cm, 1), heightFt: fi ? String(fi.feet) : '', heightIn: fi ? String(fi.inches) : '',
    unit_system: p.unit_system, timezone: p.timezone,
    cycle_start: p.cycle_last_period_start ?? '', cycle_length: str(p.cycle_length_days),
  };
}

function AboutYouCard(): ReactElement {
  const { source, profile, setProfile, today, toast, bump } = useApp();
  const [f, setF] = useState<AboutForm>(() => formFromProfile(profile));
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const units: UnitSystem = f.unit_system;
  useEffect(() => { setF(formFromProfile(profile)); }, [profile]);
  const set = <K extends keyof AboutForm>(k: K, v: AboutForm[K]): void => setF((x) => ({ ...x, [k]: v }));

  const save = async (): Promise<void> => {
    setError(null);
    const height: number | null = units === 'imperial'
      ? (f.heightFt.trim() === '' && f.heightIn.trim() === '' ? null : feetInchesToCm(Number(f.heightFt) || 0, Number(f.heightIn) || 0))
      : numOrNull(f.heightCm);
    if (height !== null && (height < 50 || height > 272)) { setError('Height looks off (allowed 50–272 cm / 1′8″–8′11″).'); return; }
    if (f.birth_date && ageOn(f.birth_date, today) < 13) { setError('Birth date must be at least 13 years ago.'); return; }
    const cycleStart: string | null = f.cycle_start === '' ? null : f.cycle_start;
    const cycleLength: number | null = numOrNull(f.cycle_length);
    const cycleOn: boolean = f.sex !== 'male' && (cycleStart !== null || cycleLength !== null);
    if (cycleOn) {
      if (cycleStart === null || cycleLength === null) { setError('Enter both the start of your last period and your usual cycle length, or leave both empty.'); return; }
      if (cycleStart > today) { setError('The last period can’t start in the future.'); return; }
      if (!Number.isInteger(cycleLength) || cycleLength < 20 || cycleLength > 45) { setError('Cycle length must be a whole number of days from 20 to 45.'); return; }
    }
    const patch: ProfileUpdate = {
      display_name: f.display_name.trim() === '' ? null : f.display_name.trim().slice(0, 80),
      sex: f.sex === '' ? null : f.sex,
      birth_date: f.birth_date === '' ? null : f.birth_date,
      height_cm: height === null ? null : Math.round(height * 10) / 10,
      unit_system: f.unit_system,
      timezone: f.timezone,
      cycle_last_period_start: cycleOn ? cycleStart : null,
      cycle_length_days: cycleOn ? cycleLength : null,
    };
    setSaving(true);
    try {
      const p: Profile = await source.updateProfile(patch);
      const cycleDropped: boolean = cycleOn && p.cycle_length_days === null;
      setProfile(p);
      if (cycleDropped) setError('Everything else was saved. The cycle settings need a database update first: run migrations/20260922210000_diet_cycle_profile.sql in the Supabase SQL Editor.');
      toast('Saved');
      bump();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="About you" sub="used for your resting burn and day boundaries">
      <div className="form-grid">
        <label className="field"><span>Name</span><input className="input" value={f.display_name} maxLength={80} onChange={(e) => set('display_name', e.target.value)} /></label>
        <label className="field"><span>Sex</span>
          <select className="input" value={f.sex} onChange={(e) => set('sex', e.target.value as Sex | '')}><option value="">—</option><option value="male">Male</option><option value="female">Female</option></select>
          <small>Used by the BMR and body-fat equations (biological sex).</small>
        </label>
        <label className="field"><span>Birth date</span><input className="input" type="date" value={f.birth_date} max={today} onChange={(e) => set('birth_date', e.target.value)} /></label>
        {units === 'imperial' ? (
          <label className="field"><span>Height</span>
            <div className="input-row">
              <div className="input-suffix"><input className="input" type="number" inputMode="numeric" value={f.heightFt} onChange={(e) => set('heightFt', e.target.value)} aria-label="Feet" /><em>ft</em></div>
              <div className="input-suffix"><input className="input" type="number" inputMode="decimal" value={f.heightIn} onChange={(e) => set('heightIn', e.target.value)} aria-label="Inches" /><em>in</em></div>
            </div>
          </label>
        ) : (
          <label className="field"><span>Height</span><div className="input-suffix"><input className="input" type="number" inputMode="decimal" value={f.heightCm} onChange={(e) => set('heightCm', e.target.value)} /><em>cm</em></div></label>
        )}
        <label className="field"><span>Units</span>
          <select className="input" value={f.unit_system} onChange={(e) => {
            const next = e.target.value as UnitSystem;
            const cm: number | null = f.unit_system === 'imperial' ? (f.heightFt || f.heightIn ? feetInchesToCm(Number(f.heightFt) || 0, Number(f.heightIn) || 0) : null) : numOrNull(f.heightCm);
            const fi = cm !== null ? cmToFeetInches(cm) : null;
            setF((x) => ({ ...x, unit_system: next, heightCm: cm !== null ? str(cm, 1) : '', heightFt: fi ? String(fi.feet) : '', heightIn: fi ? String(fi.inches) : '' }));
          }}><option value="imperial">Imperial (lb, ft/in)</option><option value="metric">Metric (kg, cm)</option></select>
        </label>
        <label className="field"><span>Time zone</span>
          <select className="input" value={f.timezone} onChange={(e) => set('timezone', e.target.value)}>
            {(timeZones().includes(f.timezone) ? timeZones() : [f.timezone, ...timeZones()]).map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
          <small>Decides which calendar day a late-night snack counts toward.</small>
        </label>
      </div>
      {f.sex !== 'male' && (
        <div className="form-grid" style={{ marginTop: 16 }}>
          <label className="field"><span>Last period started</span>
            <input className="input" type="date" max={today} value={f.cycle_start} onChange={(e) => set('cycle_start', e.target.value)} />
            <small>Optional. Adds the cycle to the water-weight estimate on Body.</small>
          </label>
          <label className="field"><span>Usual cycle length</span>
            <div className="input-suffix"><input className="input" type="number" inputMode="numeric" min={20} max={45} step={1} placeholder="28" value={f.cycle_length} onChange={(e) => set('cycle_length', e.target.value)} /><em>days</em></div>
            <small>Leave both empty to turn it off.</small>
          </label>
        </div>
      )}
      {error !== null && <div className="note err" style={{ marginTop: 14 }}>{error}</div>}
      <div className="row" style={{ marginTop: 16, justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving}>{saving ? <Spinner /> : <Icon name="check" size={18} />} Save</button>
      </div>
    </Card>
  );
}
